import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { User } from '../../entities/user/user';
import { ApolloService } from '../../services/apollo.service';
import { RolesService } from '../../services/roles/roles.service';
import { ToastService } from '../../services/toast/toast.service';
import { UsersService } from '../../services/users/users.service';
import * as fromCatalog from '../notification-settings-list/graphql';
import { FilterOption } from './notification-labels';
import {
  DIAGNOSTICS_TAB_QUERY_PARAM,
  DiagnosticsTab,
  diagnosticsTabFor,
  diagnosticsTabForIndex,
  diagnosticsTabIndex,
  diagnosticsTabQueryValue
} from './notification-diagnostics-tabs';

/**
 * Benachrichtigungs-Diagnose fuer Support (#737).
 *
 * Haelt Titelzeile, Brotkrume und alles, was beide Ebenen teilen. Nutzerliste und Typ-Katalog
 * liegen hier und nicht in den Listen: beide Tabs brauchen dieselben Daten, und ein Tabwechsel
 * soll sie nicht erneut holen.
 */
@Component({
  selector: 'app-notification-diagnostics-page',
  templateUrl: './notification-diagnostics-page.component.html',
  styleUrls: ['./notification-diagnostics-page.component.scss']
})
export class NotificationDiagnosticsPageComponent implements OnInit {

  private readonly apolloService = inject(ApolloService);
  private readonly usersService = inject(UsersService);
  private readonly rolesService = inject(RolesService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly users = signal<User[]>([]);
  readonly typeOptions = signal<FilterOption[]>([]);

  readonly activeTab = signal<DiagnosticsTab>('messages');
  readonly activeTabIndex = computed(() => diagnosticsTabIndex(this.activeTab()));

  /**
   * Client-Dok §6.6: nur im dev-Profil wirksam, Introspektion sagt darueber nichts aus — deshalb
   * ohnehin hinter derselben Rolle wie der Testversand versteckt, nicht nur ausprobiert.
   */
  readonly canSeedDemo = this.rolesService.hasRole('framework_nexato_employee');
  readonly seedingDemo = signal<boolean>(false);

  ngOnInit(): void {
    this.loadUsers();
    this.loadTypeOptions();

    // Ueber queryParamMap statt snapshot, damit eine Navigation auf denselben Screen mit anderem
    // Parameter greift — Angular verwendet die Komponenteninstanz dabei wieder.
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.activeTab.set(diagnosticsTabFor(params.get(DIAGNOSTICS_TAB_QUERY_PARAM)));
      });
  }

  /**
   * p-tabView meldet `activeIndexChange` auch dann, wenn der Index programmatisch gesetzt wurde.
   * Ohne diese Wache liefe der Wechsel doppelt: navigate setzt den Parameter, der Parameter setzt
   * das Signal, das Signal aendert activeTabIndex, der TabView meldet erneut.
   *
   * replaceUrl, damit Hin- und Herschalten die Zurueck-Taste nicht vollmuellt.
   */
  onTabChange(index: number): void {
    const tab = diagnosticsTabForIndex(index);
    if (tab === this.activeTab()) {
      return;
    }
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { [DIAGNOSTICS_TAB_QUERY_PARAM]: diagnosticsTabQueryValue(tab) },
        queryParamsHandling: 'merge',
        replaceUrl: true
      })
      .catch((error) => console.warn('notification-diagnostics: Navigation abgewiesen', error));
  }

  /**
   * §6.6: eine Ablehnung (ausserhalb des dev-Profils) ist der Normalfall, nicht der Fehlerfall
   * der Oberflaeche — deshalb der neutrale statt alarmierende Toast-Text. `userId` bleibt
   * unbenutzt, der Aufruf legt die Demo-Daten fuer den aufrufenden Nutzer selbst an.
   */
  onSeedDemo(): void {
    if (this.seedingDemo()) {
      return;
    }
    this.seedingDemo.set(true);

    this.apolloService
      .mutateObservable<fromCatalog.SeedDemoNotificationsResponse>(fromCatalog.SEED_DEMO_NOTIFICATIONS_MUTATION, {})
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const count = result?.data?.seedDemoNotifications ?? 0;
          this.toastService.addToastWithMessage(
            this.translate.instant('core.notificationDiagnostics.seedDemo.successToastMessage', { count })
          );
          this.seedingDemo.set(false);
        },
        error: () => {
          this.toastService.addErrorToast('core.notificationDiagnostics.seedDemo.errorToastMessage');
          this.seedingDemo.set(false);
        }
      });
  }

  /** Faellt sie aus, bleiben die UUIDs in den Zeilen stehen — die Listen funktionieren weiter. */
  private loadUsers(): void {
    this.usersService
      .getUsers()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (users) => this.users.set(users ?? []),
        error: () => this.users.set([])
      });
  }

  /**
   * typeKey-Optionen aus dem Katalog (#735-Query, wiederverwendet). Der Katalog ist die
   * vollstaendige Typ-Registry und fuer alle Nutzer identisch. Damit sind die Werte real und
   * Tippfehler ausgeschlossen.
   */
  private loadTypeOptions(): void {
    this.apolloService
      .query<fromCatalog.CatalogQueryResponse>(fromCatalog.NOTIFICATION_CATALOG_QUERY, {
        locale: this.translate.currentLang ?? this.translate.getDefaultLang() ?? 'de'
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const catalog = result?.data?.catalog ?? [];
          this.typeOptions.set(
            catalog.map((type) => ({
              // label kommt fertig uebersetzt vom Server — NICHT durch die translate-Pipe schicken.
              // Der technische Schluessel steht daneben, weil die Zeilen ihn ebenfalls so zeigen.
              label: `${type.label} (${type.typeKey})`,
              value: type.typeKey
            }))
          );
        },
        error: () => this.typeOptions.set([])
      });
  }
}
